/**
 * The SEM SAT/ACT form test: on the non-tutor campaigns, one ad URL serves either the standard form
 * (control) or the test prep form (test), by the PostHog flag below. Ads and their URLs don't change.
 *
 * Kill switch: set the flag to 100% control, or turn it off. Anything without a variant (PostHog slow
 * or down, bots, flag off) gets the standard form and isn't counted.
 *
 * Edge-safe: imported by the middleware and the browser's PostHog client.
 */
export const SEM_FORM_FLAG_KEY = 'sem-sat-act-non-tutor-form-test';

export const SEM_FORM_VARIANTS = ['control', 'test'] as const;
export type SemFormVariant = (typeof SEM_FORM_VARIANTS)[number];

/** The assigned variant, so the visitor's events can be tagged and a failed flag call can keep it. */
export const SEM_FORM_VARIANT_COOKIE = 'vt_sem_form_variant';
/** The distinct_id used for assignment, for visitors without a PostHog cookie yet. */
export const SEM_FORM_DISTINCT_ID_COOKIE = 'vt_sem_form_did';
/** ?sem_form_variant=control|test forces a variant for QA, without counting or saving it. */
export const SEM_FORM_QA_PARAM = 'sem_form_variant';

/** Search | SAT or ACT | Non-Tutor Tier 1 and Tier 2. */
export const SEM_FORM_CAMPAIGN_IDS: ReadonlySet<string> = new Set(['23920536885', '23920536888', '23930197135', '23930197138']);
/** SAT | Competitor, in the SAT Tier 1 campaign: it has no tutor/non-tutor split, so it stays out. */
export const SEM_FORM_EXCLUDED_AD_GROUP_IDS: ReadonlySet<string> = new Set(['201152382161']);

const SEM_PAGE = /^(?:\/df)?\/national-(sat|act)-(prep|classes|courses|lessons)\/?$/;

/** The test prep form's path for a standard SEM page (with /df), or null if the page isn't in the test. */
export const semFormTestPath = (pathname: string): string | null => {
  const match = SEM_PAGE.exec(pathname);
  return match ? `/df/test-prep/${match[1]}-${match[2]}` : null;
};

export const isSemFormVariant = (value: unknown): value is SemFormVariant => SEM_FORM_VARIANTS.includes(value as SemFormVariant);

/** Whether this landing URL's ad is in the test. */
export const isSemFormAd = (searchParams: URLSearchParams): boolean => {
  const campaignId = searchParams.get('gad_campaignid');
  const adGroupId = searchParams.get('adgroupid');
  return Boolean(campaignId && SEM_FORM_CAMPAIGN_IDS.has(campaignId)) && !(adGroupId && SEM_FORM_EXCLUDED_AD_GROUP_IDS.has(adGroupId));
};

const cookieValue = (cookies: string, name: string): string | undefined => {
  const match = cookies.split('; ').find((cookie) => cookie.startsWith(`${name}=`));
  return match?.slice(name.length + 1) || undefined;
};

export const readSemFormCookies = (cookies: string): { variant?: SemFormVariant; distinctId?: string } => {
  const variant = cookieValue(cookies, SEM_FORM_VARIANT_COOKIE);
  return { variant: isSemFormVariant(variant) ? variant : undefined, distinctId: cookieValue(cookies, SEM_FORM_DISTINCT_ID_COOKIE) };
};

export const hasPostHogCookie = (cookies: string): boolean => cookies.split('; ').some((cookie) => cookie.startsWith('ph_phc_') && cookie.split('=')[0].endsWith('_posthog'));
